import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, TrendingUp, Users, Eye, Download, Share2, 
  RefreshCw, Calendar, ArrowLeft, Activity, Zap, Clock
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const ToolsAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolStats, setToolStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, dashboardRes] = await Promise.all([
        api.get('/analytics/tools/summary'),
        api.get('/analytics/dashboard')
      ]);
      setSummary(summaryRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchToolStats = async (toolName) => {
    try {
      const response = await api.get(`/analytics/tools/${toolName}/stats?days=7`);
      setToolStats(response.data);
      setSelectedTool(toolName);
    } catch (error) {
      toast.error('Failed to load tool stats');
    }
  };

  const toolIcons = {
    'whatsapp-link': '💬',
    'invoice': '📄',
    'testimonials': '⭐',
    'qr-code': '📱',
    'bio-link': '🔗',
    'emi-calculator': '🧮',
    'business-card': '💼'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="tools-analytics-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/20 via-background to-purple-500/10 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Tools Usage Analytics</h1>
                <p className="text-muted-foreground">Track tool popularity and user engagement</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-white/10 rounded-xl flex items-center gap-2 hover:bg-white/20 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-6 text-center">
            <Activity className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-blue-500">{summary?.summary?.total_usage || 0}</p>
            <p className="text-sm text-muted-foreground">Total Usage</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Users className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-green-500">{summary?.summary?.total_unique_users || 0}</p>
            <p className="text-sm text-muted-foreground">Unique Users</p>
          </div>
          <div className="glass-card p-6 text-center">
            <Zap className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-yellow-500">{summary?.summary?.today_total || 0}</p>
            <p className="text-sm text-muted-foreground">Today's Usage</p>
          </div>
          <div className="glass-card p-6 text-center">
            <BarChart3 className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-purple-500">{summary?.summary?.total_tools || 0}</p>
            <p className="text-sm text-muted-foreground">Active Tools</p>
          </div>
        </div>

        {/* Action Breakdown */}
        {dashboard?.action_breakdown && (
          <div className="glass-card p-6 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Action Breakdown
            </h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-blue-500/10 rounded-xl text-center">
                <Eye className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-500">{dashboard.action_breakdown.view || 0}</p>
                <p className="text-xs text-muted-foreground">Views</p>
              </div>
              <div className="p-4 bg-green-500/10 rounded-xl text-center">
                <Zap className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-500">{dashboard.action_breakdown.generate || 0}</p>
                <p className="text-xs text-muted-foreground">Generates</p>
              </div>
              <div className="p-4 bg-yellow-500/10 rounded-xl text-center">
                <Download className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-500">{dashboard.action_breakdown.download || 0}</p>
                <p className="text-xs text-muted-foreground">Downloads</p>
              </div>
              <div className="p-4 bg-purple-500/10 rounded-xl text-center">
                <Share2 className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-500">{dashboard.action_breakdown.share || 0}</p>
                <p className="text-xs text-muted-foreground">Shares</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Tools List */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Tool Rankings
            </h3>
            <div className="space-y-3">
              {summary?.tools?.map((tool, index) => (
                <button
                  key={tool.tool_name}
                  onClick={() => fetchToolStats(tool.tool_name)}
                  className={`w-full p-4 rounded-xl flex items-center justify-between hover:bg-white/10 transition-colors ${
                    selectedTool === tool.tool_name ? 'bg-primary/20 border border-primary/30' : 'bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl">{toolIcons[tool.tool_name] || '🔧'}</span>
                    <div className="text-left">
                      <p className="font-medium">{tool.tool_name.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                      <p className="text-xs text-muted-foreground">
                        {tool.unique_users_count} users • Today: {tool.today_usage || 0}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-primary">{tool.total_usage}</p>
                    <p className="text-xs text-muted-foreground">total uses</p>
                  </div>
                </button>
              ))}
              {(!summary?.tools || summary.tools.length === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  No usage data yet. Tools will appear here once users start using them.
                </div>
              )}
            </div>
          </div>

          {/* Tool Details */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              {selectedTool ? `${selectedTool.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} - Last 7 Days` : 'Select a Tool'}
            </h3>
            
            {toolStats ? (
              <div className="space-y-6">
                {/* Daily Chart */}
                <div>
                  <p className="text-sm text-muted-foreground mb-3">Daily Usage</p>
                  <div className="flex items-end gap-2 h-32">
                    {toolStats.daily_stats.map((day, i) => {
                      const maxCount = Math.max(...toolStats.daily_stats.map(d => d.count), 1);
                      const height = (day.count / maxCount) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div 
                            className="w-full bg-primary/60 rounded-t-lg transition-all hover:bg-primary"
                            style={{ height: `${height}%`, minHeight: day.count > 0 ? '8px' : '0' }}
                          ></div>
                          <span className="text-xs text-muted-foreground">{day._id?.slice(5) || ''}</span>
                          <span className="text-xs font-medium">{day.count}</span>
                        </div>
                      );
                    })}
                    {toolStats.daily_stats.length === 0 && (
                      <div className="flex-1 text-center text-muted-foreground py-8">
                        No data for this period
                      </div>
                    )}
                  </div>
                </div>

                {/* Top Users */}
                {toolStats.top_users?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Top Users</p>
                    <div className="space-y-2">
                      {toolStats.top_users.slice(0, 5).map((user, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">
                              {i + 1}
                            </span>
                            <span className="text-sm">{user.name || 'Anonymous'}</span>
                          </div>
                          <span className="text-sm font-medium text-primary">{user.count} uses</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Activity */}
                {toolStats.recent_usage?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-3">Recent Activity</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {toolStats.recent_usage.slice(0, 10).map((usage, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              usage.action === 'view' ? 'bg-blue-500/20 text-blue-400' :
                              usage.action === 'generate' ? 'bg-green-500/20 text-green-400' :
                              usage.action === 'download' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-purple-500/20 text-purple-400'
                            }`}>
                              {usage.action}
                            </span>
                            <span className="text-muted-foreground">{usage.user_name || 'Guest'}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(usage.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Click on a tool to see detailed analytics</p>
              </div>
            )}
          </div>
        </div>

        {/* Hourly Distribution */}
        {dashboard?.hourly_distribution?.length > 0 && (
          <div className="glass-card p-6 mt-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Peak Hours (All Tools)
            </h3>
            <div className="flex items-end gap-1 h-24">
              {dashboard.hourly_distribution.map((hour, i) => {
                const maxCount = Math.max(...dashboard.hourly_distribution.map(h => h.count), 1);
                const height = (hour.count / maxCount) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-gradient-to-t from-blue-500 to-purple-500 rounded-t opacity-70 hover:opacity-100 transition-opacity"
                      style={{ height: `${height}%`, minHeight: hour.count > 0 ? '4px' : '0' }}
                      title={`${hour.hour}:00 - ${hour.count} uses`}
                    ></div>
                    {i % 4 === 0 && (
                      <span className="text-xs text-muted-foreground mt-1">{hour.hour}</span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">Hours (UTC)</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolsAnalytics;
